import axios from 'axios';
import { tokenUtils } from '../utils/tokenUtils';

const USER_VIDEO_UPLOADER_ID = 3;
const USER_DOC_UPLOADER_ID = 2;

class CoursesService {
  constructor() {
    this.apiUrl = process.env.REACT_APP_API_URL;
    this.endpoint = '/Course';
  }

  stripHtmlTags(html) {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  getAuthHeaders(navigate) {
    const token = tokenUtils.get();
    if (!token || tokenUtils.isExpired(token)) {
      const error = new Error('Sesión expirada');
      this.handleSessionExpired(error, navigate);
      throw error;
    }

    return { Authorization: `Bearer ${token}` };
  }

  handleSessionExpired(error, navigate) {
    tokenUtils.clearAuth();
    if (navigate) navigate('/');
    else window.location.href = '/';
  }

  async formatCourseData(formData, videos, documents, audienceTargets, selectedQuestionnaires, isUpdate = false) {
    const courseFormData = new FormData();

    // Título siempre en texto plano (sin etiquetas HTML del editor)
    courseFormData.append('Title', this.stripHtmlTags(formData.title || '').trim());
    // Conservar HTML del editor (negrita, listas, saltos de línea)
    courseFormData.append('Description', formData.description || '');
    courseFormData.append('Objective', formData.guide || '');
    courseFormData.append('TeacherId', formData.teacher?.id || '');
    courseFormData.append('Type', 'standard');
      courseFormData.append('Status', formData.status !== undefined ? formData.status : 0);
      courseFormData.append('IsPublic', formData.isPublic === true ? 'true' : 'false');
      courseFormData.append('IsWelcome', formData.isWelcome === true ? 'true' : 'false');

    // Handle thumbnail for updates vs creates
    if (isUpdate) {
      // In updates:
      // - File => replace thumbnail
      // - thumbnailRemoved flag => remove thumbnail
      // - otherwise => keep existing thumbnail (do not send Thumbnail field)
      if (formData.image && formData.image instanceof File) {
        courseFormData.append('Thumbnail', formData.image);
      } else if (formData.thumbnailRemoved === true) {
        courseFormData.append('Thumbnail', '');
      }
    } else {
      // For creation, only append Thumbnail if it's a File object (new upload)
      if (formData.image && formData.image instanceof File) {
        courseFormData.append('Thumbnail', formData.image);
      }
    }

    selectedQuestionnaires?.forEach((q, i) => {
      courseFormData.append(`QuestionnaireIds[${i}]`, q.id);
    });

    // audienceTargets already uses label IDs as keys, so just extract the selected ones
    const selectedLabelIds = Object.entries(audienceTargets)
      .filter(([labelId, isSelected]) => isSelected)
      .map(([labelId]) => parseInt(labelId));

    selectedLabelIds.forEach((labelId, i) => {
      courseFormData.append(`LabelIds[${i}]`, labelId);
    });

    // Con ReplaceLabels el backend toma estas listas como la selección completa (permite vaciarlas)
    if (Array.isArray(formData.labelGroupIds)) {
      formData.labelGroupIds.forEach((groupId, i) => {
        courseFormData.append(`LabelGroupIds[${i}]`, groupId);
      });
      courseFormData.append('ReplaceLabels', 'true');
    }

    this._appendVideos(courseFormData, videos, formData.image);
    this._appendDocuments(courseFormData, documents);

    return courseFormData;
  }

  _appendVideos(formData, videos, thumbnail) {
    console.log('=== Appending videos to FormData ===');
    console.log('Total videos received:', videos?.length || 0);
    console.log('Videos data:', videos);
    
    const validVideos = videos.filter(v => {
      const hasSource = v.source;
      const hasSelectedItem = v.selectedItem;
      const hasFile = v.file;
      const isValid = hasSource && (hasSelectedItem || hasFile);
      
      console.log('Video validation:', {
        name: v.name,
        source: v.source,
        hasSelectedItem,
        hasFile,
        isValid
      });
      
      return isValid;
    });
    
    console.log('Valid videos to append:', validVideos.length);
    
    validVideos.forEach((video, i) => {
      const key = `Videos[${i}]`;
      console.log(`Processing video ${i}:`, video);
      
      if (video.source === 'existente' && video.selectedItem) {
        console.log(`Adding existing video: ${video.selectedItem.title || video.name}`);
        formData.append(`${key}.Id`, video.selectedItem.id);
      } else if (video.source === 'nuevo' && video.file) {
        console.log(`Adding new video: ${video.name}`);
        formData.append(`${key}.Title`, video.name);
        formData.append(`${key}.Description`, video.name);
        formData.append(`${key}.UploadedById`, USER_VIDEO_UPLOADER_ID);
        formData.append(`${key}.Video`, video.file);
        if (thumbnail) {
          formData.append(`${key}.Thumbnail`, thumbnail);
        }
      } else {
        console.warn('Video skipped due to invalid configuration:', video);
      }
    });
    
    console.log('=== Video append completed ===');
  }

  _appendDocuments(formData, documents) {
    documents
      .filter(doc => doc.source && (doc.selectedItem || doc.file))
      .forEach((doc, i) => {
        const key = `Documents[${i}]`;
        if (doc.source === 'existente' && doc.selectedItem) {
          formData.append(`${key}.Id`, doc.selectedItem.id);
        } else if (doc.source === 'nuevo' && doc.file) {
          formData.append(`${key}.Title`, doc.name);
          formData.append(`${key}.Description`, doc.name);
          formData.append(`${key}.UploadedById`, USER_DOC_UPLOADER_ID);
          formData.append(`${key}.File`, doc.file);
        }
      });
  }

  async createCourse(formData, videos, documents, audienceTargets, selectedQuestionnaires, navigate, status = null) {
    try {
      const courseData = await this.formatCourseData(formData, videos, documents, audienceTargets, selectedQuestionnaires);
      
      // Si se proporciona un status, agregarlo al FormData
      if (status !== null) {
        courseData.append('Status', status);
      }
      
       const response = await axios.post(`${this.apiUrl}${this.endpoint}`, courseData, {
         headers: this.getAuthHeaders(navigate)
       });
       return response.data;
    } catch (error) {
      this._handleRequestError(error, 'crear curso');
      throw error;
    }
  }

  async duplicateCourse(courseId, navigate) {
    try {
      const response = await axios.post(
        `${this.apiUrl}${this.endpoint}/${courseId}/duplicate`,
        null,
        { headers: this.getAuthHeaders(navigate) }
      );
      return this.normalizeCourse(response.data) || response.data;
    } catch (error) {
      this._handleRequestError(error, `duplicar curso ${courseId}`);
      throw error;
    }
  }

  async updateCourse(courseId, formData, videos, documents, audienceTargets, selectedQuestionnaires, navigate) {
    try {
      console.log('=== Starting course update ===');
      console.log('Course ID:', courseId);
      console.log('Form data:', formData);
      console.log('Videos count:', videos?.length || 0);
      console.log('Documents count:', documents?.length || 0);
      
      const courseData = await this.formatCourseData(formData, videos, documents, audienceTargets, selectedQuestionnaires, true);
      
      console.log('FormData entries:');
      for (let [key, value] of courseData.entries()) {
        console.log(`${key}:`, value);
      }
      
      const response = await axios.put(`${this.apiUrl}${this.endpoint}/${courseId}`, courseData, {
        headers: this.getAuthHeaders(navigate)
      });
      
      console.log('Update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating course:', error);
      this._handleRequestError(error, `actualizar curso ${courseId}`);
      throw error;
    }
  }

  async publishCourse(courseId, newStatus, navigate) {
    try {
      const response = await axios.post(`${this.apiUrl}${this.endpoint}/change-status`, {
        courseId: courseId,
        newStatus: newStatus
      }, {
        headers: {
          ...this.getAuthHeaders(navigate),
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, `cambiar estado del curso ${courseId}`);
      throw error;
    }
  }

  async deleteCourse(courseId, navigate) {
    try {
      const response = await axios.delete(`${this.apiUrl}${this.endpoint}/${courseId}`, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, `eliminar curso ${courseId}`);
      throw error;
    }
  }

  async getCourses(filters = {}, navigate) {
    try {
      const params = new URLSearchParams(filters).toString();
      const url = `${this.apiUrl}${this.endpoint}${params ? `?${params}` : ''}`;
      const response = await axios.get(url, { headers: this.getAuthHeaders(navigate) });
      return response.data;
    } catch (error) {
      this._handleAuthError(error);
      throw error;
    }
  }

  normalizeCourse(course, fallbackId = null) {
    if (!course || typeof course !== 'object') return null;
    const id =
      course.id ??
      course.Id ??
      course.courseId ??
      course.CourseId ??
      fallbackId;
    return {
      ...course,
      id: id != null && id !== '' ? id : undefined,
      title: course.title ?? course.Title ?? '',
      description: course.description ?? course.Description ?? '',
      thumbnailPath:
        course.thumbnailPath ??
        course.thumbnailUrl ??
        course.ThumbnailPath ??
        course.thumbnail,
      teacherName:
        course.teacherName ?? course.teacher ?? course.TeacherName ?? '',
      teacherId:
        course.teacherId ??
        course.TeacherId ??
        course.teached_id ??
        course.TeachedId,
      createdAt: course.createdAt ?? course.CreatedAt,
      isRecommended: Boolean(course.isRecommended ?? course.IsRecommended),
      isPublic: Boolean(course.isPublic ?? course.IsPublic),
      isWelcome: Boolean(course.isWelcome ?? course.IsWelcome),
      status: course.status ?? course.Status,
    };
  }

  async getWelcomeStatus(userId, navigate) {
    try {
      const response = await axios.get(
        `${this.apiUrl}/UserCourse/welcome-status/${userId}`,
        { headers: this.getAuthHeaders(navigate) }
      );
      const data = response.data || {};
      return {
        hasWelcomeCourse: Boolean(data.hasWelcomeCourse ?? data.HasWelcomeCourse),
        courseId: data.courseId ?? data.CourseId ?? null,
        courseTitle: data.courseTitle ?? data.CourseTitle ?? null,
        isEnrolled: Boolean(data.isEnrolled ?? data.IsEnrolled),
        isCompleted: Boolean(data.isCompleted ?? data.IsCompleted),
        canAccessOtherCourses: Boolean(
          data.canAccessOtherCourses ?? data.CanAccessOtherCourses
        ),
      };
    } catch (error) {
      this._handleRequestError(error, 'obtener estado del curso de bienvenida');
      throw error;
    }
  }

  normalizeCoursesList(data) {
    const list = Array.isArray(data)
      ? data
      : (data?.courses || data?.data || []);
    return list.map((course) => this.normalizeCourse(course)).filter(Boolean);
  }

  async getPublicCourses() {
    try {
      const response = await axios.get(`${this.apiUrl}${this.endpoint}/public`);
      return this.normalizeCoursesList(response.data || []);
    } catch (error) {
      console.error("Error fetching public courses:", error);
      return [];
    }
  }

  async getPublicCourseById(courseId) {
    try {
      const response = await axios.get(`${this.apiUrl}${this.endpoint}/public/${courseId}`);
      return this.normalizeCourse(response.data, courseId);
    } catch (error) {
      return null;
    }
  }

  async resolveSharedCourse(courseId, navigate) {
    const token = tokenUtils.get();
    const hasValidSession = Boolean(token && !tokenUtils.isExpired(token));

    if (hasValidSession) {
      try {
        const response = await axios.get(`${this.apiUrl}${this.endpoint}/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return this.normalizeCourse(response.data, courseId);
      } catch (error) {
        console.warn('No se pudo cargar curso compartido con sesión:', error);
      }
    }

    const publicCourse = await this.getPublicCourseById(courseId);
    if (publicCourse?.id != null) return publicCourse;

    return null;
  }

  async getCoursesByTeacher(teacherId, navigate) {
    try {
      const response = await axios.get(`${this.apiUrl}${this.endpoint}/teacher/${teacherId}`, {
        headers: this.getAuthHeaders(navigate)
      });
      return this.normalizeCoursesList(response.data);
    } catch (error) {
      this._handleAuthError(error);
      throw error;
    }
  }

async getCourseById(courseId, navigate) {
  try {
    const response = await axios.get(`${this.apiUrl}${this.endpoint}/${courseId}`, {
      headers: this.getAuthHeaders(navigate)
    });
    return this.normalizeCourse(response.data, courseId);
  } catch (error) {
    this._handleAuthError(error);
    throw error;
  }
}


  async assignUserCourse(userId, courseId, navigate) {
    try {
      const payload = { userId, courseId };
      const response = await axios.post(`${this.apiUrl}/UserCourse/assign-course`, payload, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'asignar curso');
      throw error;
    }
  }

  async getUserCourses(userId, navigate) {
    try {
      const response = await axios.get(`${this.apiUrl}/UserCourse/user/${userId}/courses`, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'obtener cursos del usuario');
      throw error;
    }
  }

  async getSuggestedCourses(userId, navigate, page = 1, pageSize = 10) {
    try {
      const response = await axios.get(`${this.apiUrl}/Course/recommendations/${userId}?page=${page}&pageSize=${pageSize}`, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'obtener cursos sugeridos');
      throw error;
    }
  }

  // Método que usa usuarioConCedula directamente
  async getUserCoursesWithCedula(usuarioConCedula, navigate) {
    const userId = usuarioConCedula?.id ?? usuarioConCedula?.Id;
    if (!userId) {
      throw new Error('Usuario no disponible');
    }
    
    try {
      const response = await axios.get(`${this.apiUrl}/UserCourse/user/${userId}/courses`, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'obtener cursos del usuario');
      throw error;
    }
  }

  getCourseProgress(userId, courseId, navigate) {
    return axios.get(`${this.apiUrl}/UserCourse/course-progress/${userId}/${courseId}`, {
      headers: this.getAuthHeaders(navigate)
    });
  }

  async markVideoAsWatched(userId, courseId, videoId) {
    try {
      const response = await axios.post(
        `${this.apiUrl}/UserCourse/mark-video-watched`,
        { userId, courseId, videoId },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error marking video as watched:', error);
      throw error;
    }
  }

  async getCoursePendingRequests (courseId, navigate) {
    try {
      const response = await axios.get(`${this.apiUrl}/UserCourse/course/${courseId}/pending-requests`, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'obtener solicitudes pendientes');
      throw error;
    }
  };

  async getCourseApprovedRequests(courseId, navigate) {
    try {
      const response = await axios.get(`${this.apiUrl}/UserCourse/course/${courseId}/approved-users`, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'obtener solicitudes aprobadas');
      throw error;
    }
  };

  async approveRequest (id, navigate) {
    try {
      // Obtener el teacherId del objeto usuarioConCedula
      const usuarioConCedula = JSON.parse(localStorage.getItem('usuarioConCedula') || '{}');
      const teacherId = usuarioConCedula.id;
      
      if (!teacherId) {
        throw new Error('No se pudo obtener el ID del profesor');
      }
      
      const response = await axios.post(`${this.apiUrl}/UserCourse/approve-request/${id}?teacherId=${teacherId}`, {}, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'aprobar solicitud');
      throw error;
    }
  };

  async denyRequest (id, navigate) {
    try {
      // Obtener el teacherId del objeto usuarioConCedula
      const usuarioConCedula = JSON.parse(localStorage.getItem('usuarioConCedula') || '{}');
      const teacherId = usuarioConCedula.id;
      
      if (!teacherId) {
        throw new Error('No se pudo obtener el ID del profesor');
      }
      
      const response = await axios.post(`${this.apiUrl}/UserCourse/deny-request/${id}?teacherId=${teacherId}`, {}, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'denegar solicitud');
      throw error;
    }
  };

  async assignDirectly(userId, courseId, navigate) {
    try {
      const response = await axios.post(`${this.apiUrl}/UserCourse/direct-assign-course`, { userId, courseId }, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'asignar curso directamente');
      throw error;
    }
  };

  async bulkImportEnrollments(rows, navigate) {
    try {
      const response = await axios.post(
        `${this.apiUrl}/UserCourse/bulk-import-enrollments`,
        { rows },
        { headers: this.getAuthHeaders(navigate) }
      );
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'importar matrículas masivas');
      throw error;
    }
  }

  async bulkImportEnrollmentsFromExcel(file, navigate, onUploadProgress) {
    try {
      const formData = new FormData();
      formData.append('File', file);
      const response = await axios.post(
        `${this.apiUrl}/UserCourse/bulk-import-enrollments/excel`,
        formData,
        {
          headers: {
            ...this.getAuthHeaders(navigate),
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress,
        }
      );
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'importar matrículas desde Excel');
      throw error;
    }
  }

  async removeUserCourse(userId, courseId, navigate) {
    try {
      const response = await axios.delete(`${this.apiUrl}/UserCourse/user/${userId}/courses/${courseId}`, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'eliminar curso del usuario');
      throw error;
    }
  };

  async getCoursesProgressByUser(userId, navigate) {
    try {
      const response = await axios.get(`${this.apiUrl}/UserCourse/user/${userId}/courses-with-progress`, {
        headers: this.getAuthHeaders(navigate)
      });
      return response.data;
    } catch (error) {
      this._handleRequestError(error, 'obtener progreso de cursos del usuario');
      throw error;
    }
  };


  _handleAuthError(error) {
    if (error.response?.status === 401) {
      tokenUtils.clearAuth();
      window.location.href = '/';
    }
  }

  _handleRequestError(error, context = '') {
    console.error(`Error al ${context}:`, error);
    if (error.response?.status === 401) this._handleAuthError(error);
  }
}

const coursesService = new CoursesService();
export default coursesService;
